import { takeLatest, call, put, select } from 'redux-saga/effects';
import axios from 'axios';
import {
  turnitinGetEulaFailure,
  turnitinGetEulaRequest,
  setEulaVersion,
} from '../reducers/turnitin';

import { API_LINK, TURNITIN_EULA_LANGUAGE, TURNITIN_EULA_VERSION_LATEST } from '../../constants';
import { setEula, showEula } from '../reducers/popi';

const API = `${API_LINK}api`;

function looksLikeVersionId(value) {
  if (typeof value !== 'string' || !value) {
    return false;
  }

  if (value.length > 64 || value.includes('<')) {
    return false;
  }

  return true;
}

function parseEulaVersion(data) {
  if (!data) {
    return null;
  }

  if (typeof data === 'string') {
    return looksLikeVersionId(data) ? data : null;
  }

  const version = data.version || data.version_id;
  return looksLikeVersionId(version) ? version : null;
}

function parseEulaHtml(data) {
  if (typeof data === 'string') {
    return data.includes('<') ? data : '';
  }

  if (!data || typeof data !== 'object') {
    return '';
  }

  if (typeof data.html === 'string') {
    return data.html;
  }

  if (typeof data.content === 'string') {
    return data.content;
  }

  if (typeof data.text === 'string') {
    return data.text;
  }

  return '';
}

function parseEulaUrl(data) {
  if (!data || typeof data !== 'object') {
    return '';
  }

  return typeof data.url === 'string' ? data.url : '';
}

/**
 * Normalise Turnitin GET /eula/latest (or backend tii_getEula proxy) into { version, html, url }.
 * Turnitin may return a single object or an array of language-specific entries. The latest
 * Turnitin response provides only a `url` to the hosted EULA HTML rather than inline content.
 */
export function parseEulaLatestResponse(raw) {
  let data = raw;

  if (typeof raw === 'string') {
    if (raw.includes('<')) {
      return {
        version: TURNITIN_EULA_VERSION_LATEST,
        html: raw,
        url: '',
      };
    }

    try {
      data = JSON.parse(raw);
    } catch (error) {
      return { version: null, html: '', url: '' };
    }
  }

  if (Array.isArray(data)) {
    const preferred = data.find(
      (item) => item && item.language === TURNITIN_EULA_LANGUAGE,
    ) || data[0];

    return {
      version: parseEulaVersion(preferred),
      html: parseEulaHtml(preferred),
      url: parseEulaUrl(preferred),
    };
  }

  return {
    version: parseEulaVersion(data),
    html: parseEulaHtml(data),
    url: parseEulaUrl(data),
  };
}

function buildEulaIframeMarkup(url) {
  return `<iframe src="${url}" title="Turnitin EULA"></iframe>`;
}

function* fetchEula(token) {
  const response = yield call(() =>
    axios({
      method: 'get',
      url: `${API}/assessor/tii_getEula`,
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Authorization: `Bearer ${token}`,
      },
      params: {
        language: TURNITIN_EULA_LANGUAGE,
      },
      credentials: 'same-origin',
    }),
  );

  return response;
}

function* workerTurnitinGetEulaSaga() {
  try {
    const state = yield select(fullState => fullState);
    const { token } = state.loginReducer;
    const eulaResponse = yield call(fetchEula, token);
    const { version, html, url } = parseEulaLatestResponse(eulaResponse.data);
    const eulaVersion = version || TURNITIN_EULA_VERSION_LATEST;
    const markup = html || (url ? buildEulaIframeMarkup(url) : '');

    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('[Turnitin] tii_getEula status:', eulaResponse.status, 'htmlLength:', html && html.length, 'url:', url, 'version:', eulaVersion);
    }

    if (eulaResponse.status === 200 && markup) {
      yield put(setEulaVersion({ version: eulaVersion }));
      yield put(setEula(markup));
      yield put(showEula(true));
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('[Turnitin] showEula(true) dispatched → popup should render');
      }
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[Turnitin] EULA not usable — popup will NOT show. status:', eulaResponse.status, 'emptyMarkup:', !markup);
    }

    if (eulaResponse.status === 201) {
      const message = eulaResponse.data && eulaResponse.data.message;
      yield put(turnitinGetEulaFailure(message || 'Unable to load Turnitin EULA'));
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('[Turnitin] tii_getEula threw:', error.response && error.response.status, error.message);
    }
    yield put(turnitinGetEulaFailure(error.message));
  }
}

export function* watcherTurnitinGetEulaSaga() {
  yield takeLatest(turnitinGetEulaRequest().type, workerTurnitinGetEulaSaga);
}
